/**
 * Accessible subject for expand/collapse controls (no Expand/Collapse/panel prefix).
 * Joins non-empty parts; formats Dates (and ISO date strings) as M/d/yy or M/d/yy H:mm.
 * Duplicate string parts are skipped (e.g. subject === category).
 */
export function getExpandSubject(
  ...parts: (string | Date | number | null | undefined)[]
): string {
  const out: string[] = [];
  for (const part of parts) {
    if (part == null || part === '') {
      continue;
    }

    if (typeof part === 'number') {
      pushUnique(out, String(part));
      continue;
    }

    const asDate = toDate(part);
    if (asDate) {
      pushUnique(out, formatExpandDate(asDate));
      continue;
    }

    if (typeof part === 'string') {
      const trimmed = part.trim();
      if (trimmed) {
        pushUnique(out, trimmed);
      }
    }
  }
  return out.join(' ');
}

/** Date-only label (M/d/yy) when the UI column is date without time. */
export function getExpandDateOnly(
  value: string | Date | number | null | undefined,
): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : formatDateOnly(d);
  }
  const asDate = toDate(value instanceof Date ? value : String(value));
  return asDate ? formatDateOnly(asDate) : '';
}

function pushUnique(out: string[], value: string): void {
  if (!out.includes(value)) {
    out.push(value);
  }
}

function toDate(value: string | Date): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  // ISO / sortable date strings only — avoid parsing free text like note types
  if (/^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatDateOnly(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
}

function formatExpandDate(d: Date): string {
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  const date = formatDateOnly(d);
  return hasTime
    ? `${date} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`
    : date;
}
